import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import {
  ArrowLeft,
  Shield,
  Save,
  User,
  Check,
  X,
  Search,
  Settings,
} from "lucide-react";
import {
  fetchEmployeePermissions,
  updateEmployeePermissions,
  clearError,
  clearUpdateSuccess,
  clearEmployeePermissions,
} from "../../RTK/employees/GetPermissions";
import Loader from "../../components/loader/Loader";

const EmployeePermissions = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const {
    employeePermissions,
    currentEmployee,
    loading,
    updateLoading,
    error,
    updateSuccess,
  } = useSelector((state) => state.permissions);

  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("الكل");

  useEffect(() => {
    dispatch(fetchEmployeePermissions(id));

    return () => {
      dispatch(clearEmployeePermissions());
      dispatch(clearError());
      dispatch(clearUpdateSuccess());
    };
  }, [dispatch, id]);

  useEffect(() => {
    if (employeePermissions.length > 0) {
      const selected = employeePermissions
        .filter((permission) => permission.has_permission)
        .map((permission) => permission.id);
      setSelectedPermissions(selected);
    }
  }, [employeePermissions]);

  useEffect(() => {
    if (updateSuccess) {
      toast.success("تم تحديث الصلاحيات بنجاح");
      dispatch(clearUpdateSuccess());
    }
  }, [updateSuccess, dispatch]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const handlePermissionToggle = (permissionId) => {
    setSelectedPermissions((prev) =>
      prev.includes(permissionId)
        ? prev.filter((id) => id !== permissionId)
        : [...prev, permissionId]
    );
  };

  const handleSavePermissions = () => {
    dispatch(
      updateEmployeePermissions({
        employeeId: id,
        permissions: selectedPermissions,
      })
    );
  };

  const handleSelectAll = (category) => {
    const categoryPermissions = filteredPermissions
      .filter((permission) => permission.category === category)
      .map((permission) => permission.id);

    const allSelected = categoryPermissions.every((id) =>
      selectedPermissions.includes(id)
    );

    if (allSelected) {
      setSelectedPermissions((prev) =>
        prev.filter((id) => !categoryPermissions.includes(id))
      );
    } else {
      setSelectedPermissions((prev) => [
        ...prev.filter((id) => !categoryPermissions.includes(id)),
        ...categoryPermissions,
      ]);
    }
  };

  if (loading) {
    return <Loader />;
  }

  if (!currentEmployee) {
    return (
      <div className="min-h-screen bg-[#0f172a] flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-400 text-xl mb-4">موظف غير موجود</div>
          <button
            onClick={() => navigate("/employees")}
            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-lg"
          >
            العودة للموظفين
          </button>
        </div>
      </div>
    );
  }

  // Group permissions by category
  const groupedPermissions = employeePermissions.reduce((acc, permission) => {
    if (!acc[permission.category]) {
      acc[permission.category] = [];
    }
    acc[permission.category].push(permission);
    return acc;
  }, {});

  // Filter permissions
  const filteredPermissions = employeePermissions.filter((permission) =>
    permission.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
    (selectedCategory === "الكل" || permission.category === selectedCategory)
  );

  const categories = ["الكل", ...Object.keys(groupedPermissions)];

  return (
    <div className="min-h-screen bg-[#0f172a] p-4 md:p-6" dir="rtl">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <button
            onClick={() => navigate("/employees")}
            className="flex items-center gap-2 text-slate-400 hover:text-white mb-4 transition-colors group"
          >
            <ArrowLeft className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            العودة للموظفين
          </button>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
                <User className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white">
                  صلاحيات {currentEmployee.username}
                </h1>
                <p className="text-slate-400">إدارة صلاحيات الموظف</p>
              </div>
            </div>

            <button
              onClick={handleSavePermissions}
              disabled={updateLoading}
              className="flex items-center gap-2 bg-gradient-to-r from-green-600 to-green-700 text-white px-6 py-3 rounded-xl hover:from-green-700 hover:to-green-800 transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {updateLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Save className="w-5 h-5" />
              )}
              حفظ التغييرات
            </button>
          </div>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50 mb-8"
        >
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              <input
                type="text"
                placeholder="البحث في الصلاحيات..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pr-12 pl-4 py-3 bg-slate-900/50 border border-slate-700 rounded-xl text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              />
            </div>

            {/* Category Filter */}
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="bg-slate-900/50 border border-slate-700 rounded-xl text-white px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            >
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          {/* Stats */}
          <div className="mt-4 flex items-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span className="text-slate-300">
                مفعلة: {selectedPermissions.length}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-slate-600 rounded-full"></div>
              <span className="text-slate-300">
                غير مفعلة: {employeePermissions.length - selectedPermissions.length}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
              <span className="text-slate-300">
                المجموع: {employeePermissions.length}
              </span>
            </div>
          </div>
        </motion.div>

        {/* Permissions by Category */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-6"
        >
          {selectedCategory === "الكل"
            ? Object.entries(groupedPermissions).map(([category, permissions]) => (
                <CategorySection
                  key={category}
                  category={category}
                  permissions={permissions}
                  selectedPermissions={selectedPermissions}
                  onPermissionToggle={handlePermissionToggle}
                  onSelectAll={handleSelectAll}
                  searchTerm={searchTerm}
                />
              ))
            : selectedCategory !== "الكل" && (
                <CategorySection
                  category={selectedCategory}
                  permissions={groupedPermissions[selectedCategory] || []}
                  selectedPermissions={selectedPermissions}
                  onPermissionToggle={handlePermissionToggle}
                  onSelectAll={handleSelectAll}
                  searchTerm={searchTerm}
                />
              )}
        </motion.div>

        {/* Empty State */}
        {filteredPermissions.length === 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-12"
          >
            <Settings className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-400 mb-2">
              لا توجد صلاحيات
            </h3>
            <p className="text-slate-500">
              لم يتم العثور على صلاحيات مطابقة للبحث
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
};

// Category Section Component
const CategorySection = ({
  category,
  permissions,
  selectedPermissions,
  onPermissionToggle,
  onSelectAll,
  searchTerm,
}) => {
  const filteredPermissions = permissions.filter((permission) =>
    permission.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (filteredPermissions.length === 0) return null;

  const allSelected = filteredPermissions.every((permission) =>
    selectedPermissions.includes(permission.id)
  );

  const getCategoryIcon = (category) => {
    const icons = {
      الطلبات: "🛒",
      المستخدمون: "👥",
      المعاملات: "💳",
      المنتجات: "📦",
      الوكلاء: "🤝",
      المصروفات: "💰",
      "طرق الدفع": "💳",
      البانرات: "🎨",
      الإحصائيات: "📊",
      الإعدادات: "⚙️",
    };
    return icons[category] || "🔐";
  };

  return (
    <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 overflow-hidden">
      {/* Category Header */}
      <div className="bg-slate-900/50 p-4 border-b border-slate-700/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{getCategoryIcon(category)}</span>
            <div>
              <h3 className="text-lg font-semibold text-white">{category}</h3>
              <p className="text-sm text-slate-400">
                {filteredPermissions.length} صلاحية
              </p>
            </div>
          </div>
          <button
            onClick={() => onSelectAll(category)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 ${
              allSelected
                ? "bg-red-600/20 text-red-400 hover:bg-red-600/30"
                : "bg-green-600/20 text-green-400 hover:bg-green-600/30"
            }`}
          >
            {allSelected ? (
              <>
                <X className="w-4 h-4" />
                إلغاء الكل
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                تحديد الكل
              </>
            )}
          </button>
        </div>
      </div>

      {/* Permissions List */}
      <div className="p-4 space-y-3">
        {filteredPermissions.map((permission, index) => (
          <motion.div
            key={permission.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.05 }}
            className={`flex items-center justify-between p-4 rounded-lg border transition-all duration-200 cursor-pointer hover:shadow-lg ${
              selectedPermissions.includes(permission.id)
                ? "bg-green-900/20 border-green-500/30 hover:bg-green-900/30"
                : "bg-slate-900/30 border-slate-700/30 hover:bg-slate-900/50"
            }`}
            onClick={() => onPermissionToggle(permission.id)}
          >
            <div className="flex-1">
              <h4 className="font-medium text-white mb-1">{permission.name}</h4>
              <p className="text-sm text-slate-400">{permission.code}</p>
            </div>
            <div
              className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                selectedPermissions.includes(permission.id)
                  ? "bg-green-500 border-green-500"
                  : "border-slate-600 hover:border-slate-500"
              }`}
            >
              {selectedPermissions.includes(permission.id) && (
                <Check className="w-4 h-4 text-white" />
              )}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default EmployeePermissions;